import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { Headline, Layout, Text, TextLink } from '@oneaudi/unified-web-components';
import { vehicleContext } from '../VehicleProvider';
import DealerNotesModal from './DealerNotesModal';
import { IS_T3_WEBSITE } from '../../constants/environment';
import {
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
  SYS_PAGE_MARGIN_XL,
  SYS_SIZE_FIXED_700,
} from '@oneaudi/unified-web-common';

const DealersNote: React.FC = () => {
  const { t } = useTranslation();
  const { vehicle } = useContext(vehicleContext);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery({ query: `(max-width: ${SYS_BREAKPOINT_MD})` });
  const isTablet = useMediaQuery({
    query: `(min-width: ${SYS_BREAKPOINT_MD}) and (max-width: ${SYS_BREAKPOINT_XL})`,
  });

  const characterLimit = isMobile ? 250 : isTablet ? 400 : 600;

  const onModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.getElementById('closeModalVDPFSS')?.focus();
    } else {
      document.getElementById('readMoreCta')?.focus();
    }
  }, [modalOpen]);

  if (!vehicle) return null;

  const { dealerName, dealerNote } = vehicle;

  return (
    <Layout
      direction={'column'}
      padding={[SYS_PAGE_MARGIN_XL, SYS_PAGE_MARGIN_XL]}
      data-testid="dealers-note-wrapper"
    >
      {!IS_T3_WEBSITE ? (
        <>
          <Headline
            tag="h1"
            variant={{
              order: '3',
              type: 'headline',
              weight: 'normal',
            }}
            data-testid="modal-header"
          >
            {dealerName}
          </Headline>
          {/* It is possible that we get html code from the backend */}
          {!!dealerNote && <Text data-testid="dealer-note-not-tier3">{dealerNote}</Text>}
        </>
      ) : (
        <>
          {!!dealerNote && (
            <Text data-testid="dealer-note-tier3">
              {dealerNote.length > characterLimit
                ? `${dealerNote.slice(0, characterLimit)}...`
                : dealerNote}
            </Text>
          )}
          {!!dealerNote && dealerNote.length > characterLimit && (
            <Layout margin={[SYS_SIZE_FIXED_700, 0, 0, 0]}>
              <TextLink
                id="readMoreCta"
                data-testid="dealer-notes-modal-button"
                aria-label={t('featuresAndSpecsSection.ariaLabel.dealerNoteTab')}
                className="className"
                href="#"
                onClick={onModalOpen}
                rel="noopener"
                size="md"
                target="_self"
                title={t('featuresAndSpecsSection.categories.dealerNotes.readMore')}
                variant="standalone"
                icon="forward"
                iconPosition="trailing"
              >
                {t('featuresAndSpecsSection.categories.dealerNotes.readMore')}
              </TextLink>
              {modalOpen && (
                <DealerNotesModal
                  copy={t('featuresAndSpecsSection.categories.dealerNotes.title')}
                  description={dealerNote}
                  onClose={onModalClose}
                />
              )}
            </Layout>
          )}
        </>
      )}
    </Layout>
  );
};

export default DealersNote;
